import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AppInput,
  AppSelect,
  OutlineButton,
  PrimaryButton,
  StatusAlertCard,
  StatusBadge,
} from '../../../../components/ui';
import {
  operationalBadge,
  resolveAssetOwner,
} from '../../../../data/master/assetOwner';
import { listEquipment } from '../../../../repositories/equipment.repository';
import { listUserEquipment } from '../../../../repositories/userEquipment.repository';
import { recommendIndentLine } from '../../../../repositories/indent.repository';
import { getConfigValue } from '../../../../repositories/systemConfig.repository';
import {
  assetMatchKind,
  assetTypeLabel,
  labelsMatch,
  lineNeedLabel,
  matchBadgeProps,
} from './recommendMatch';
import { ReferredByPicker } from './ReferredByPicker';

/**
 * Pick available vehicles to recommend for an equipment line.
 * Prefers member fleet (`user_equipments`); also includes legacy equipment assets.
 */
export function RecommendAssetsModal({
  isOpen,
  onClose,
  request,
  line,
  onSaved,
  maxRecommendations,
}) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [remarks, setRemarks] = useState({});
  const [referrals, setReferrals] = useState({});
  const [typeFilter, setTypeFilter] = useState('match');
  const [candidates, setCandidates] = useState([]);
  const [maxTotal, setMaxTotal] = useState(maxRecommendations || 3);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const existingCount = line?.recommendations?.length || 0;
  const slots = Math.max(0, maxTotal - existingCount);
  const requestTypeLabel = lineNeedLabel(line) || 'selected type';

  useEffect(() => {
    if (!isOpen || !request || !line) return;
    setSelected(new Set());
    setRemarks({});
    setReferrals({});
    setSearch('');
    const hasType = Boolean(
      line.equipmentType || line.equipmentTypeId || line.catalogLabel,
    );
    setTypeFilter(hasType ? 'match' : 'all');
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [fleetRes, legacyRes, cfgMax] = await Promise.all([
          listUserEquipment({
            pageSize: 200,
            kycStatus: 'verified',
          }),
          listEquipment({
            pageSize: 200,
            kycStatus: 'verified',
            operationalStatus: 'available',
          }).catch(() => ({ items: [] })),
          maxRecommendations != null
            ? Promise.resolve(maxRecommendations)
            : getConfigValue('maxRecommendationsPerRequest'),
        ]);
        if (cancelled) return;
        setMaxTotal(Number(cfgMax) || 3);

        const already = new Set(
          (line.recommendations || [])
            .map((r) => {
              if (r.userEquipmentId != null) return `UE-${r.userEquipmentId}`;
              return r.assetId || r.assetCode || null;
            })
            .filter(Boolean),
        );

        const fleetItems = (fleetRes.items || [])
          .filter((a) => {
            if (a.status !== 'Verified') return false;
            if (a.active === false) return false;
            if (already.has(a.assetId)) return false;
            return true;
          })
          .map((a) => ({
            ...a,
            source: 'user_equipment',
            operationalStatus: 'Available',
            ownerType: 'Member',
            peopleName: a.ownerName,
          }));

        const legacyItems = (legacyRes.items || [])
          .filter((a) => {
            if (a.status !== 'Verified') return false;
            if ((a.operationalStatus || 'Available') === 'Deployed') return false;
            if (already.has(a.assetId)) return false;
            if (a.vendorId && request.vendorId && a.vendorId === request.vendorId) {
              return false;
            }
            if (
              a.vendorDbId != null &&
              request.vendorDbId != null &&
              Number(a.vendorDbId) === Number(request.vendorDbId)
            ) {
              return false;
            }
            return true;
          })
          .map((a) => ({ ...a, source: 'equipment' }));

        setCandidates([...fleetItems, ...legacyItems]);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load equipment',
            status: 'error',
            position: 'top-right',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, request, line, maxRecommendations, toast]);

  const pool = useMemo(
    () =>
      (candidates || []).map((asset) => ({
        asset,
        owner: resolveAssetOwner(asset),
        kind: assetMatchKind(asset, line),
      })),
    [candidates, line],
  );

  const matchCount = useMemo(
    () => pool.filter((r) => r.kind === 'exact').length,
    [pool],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = pool.filter(({ asset, owner, kind }) => {
      if (typeFilter === 'match' && kind !== 'exact') return false;
      if (!q) return true;
      const hay = [
        asset.assetId,
        asset.equipmentType,
        asset.equipmentDescription,
        asset.manufacturer,
        asset.model,
        asset.regNo,
        owner.ownerName,
        owner.ownerType,
        owner.villageName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });

    filtered.sort((a, b) => {
      const rank = (k) => (k === 'exact' ? 0 : k === 'partial' ? 1 : 2);
      const byMatch = rank(a.kind) - rank(b.kind);
      if (byMatch !== 0) return byMatch;
      return String(a.asset.assetId || '').localeCompare(
        String(b.asset.assetId || ''),
      );
    });
    return filtered;
  }, [pool, typeFilter, search]);

  if (!request || !line) return null;

  const toggle = (assetId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) {
        next.delete(assetId);
        return next;
      }
      if (next.size >= slots) {
        toast({
          title: `Maximum ${maxTotal} recommendations per line`,
          status: 'warning',
          position: 'top-right',
        });
        return prev;
      }
      next.add(assetId);
      return next;
    });
  };

  const handleRecommend = async () => {
    const ids = [...selected];
    if (ids.length < 1) {
      toast({
        title: 'Select at least one vehicle',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    if (request.id == null || line.id == null) {
      toast({ title: 'Missing request/line id', status: 'error', position: 'top-right' });
      return;
    }

    setSaving(true);
    try {
      let latest = null;
      for (const assetId of ids) {
        const asset = candidates.find((a) => a.assetId === assetId);
        if (!asset?.id) continue;
        const referral = referrals[assetId] || {};
        const body =
          asset.source === 'user_equipment'
            ? {
                userEquipmentId: asset.id,
                remark: remarks[assetId] || '',
                referredByAppUserId: referral.appUserId || null,
                referredByName: referral.name || '',
              }
            : {
                equipmentId: asset.id,
                remark: remarks[assetId] || '',
                referredByAppUserId: referral.appUserId || null,
                referredByName: referral.name || '',
              };
        latest = await recommendIndentLine(request.id, line.id, body);
      }
      toast({
        title: `${ids.length} recommendation(s) added`,
        status: 'success',
        position: 'top-right',
      });
      onSaved?.(latest);
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to recommend',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      isCentered
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader pb={2}>
          Recommend equipment
          <Text fontSize="14px" fontWeight="500" color="text.secondary" mt={1}>
            Line: {requestTypeLabel} · {request.requestNo || request.indentId}. Max{' '}
            {maxTotal} recommendations per line.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
          <VStack align="stretch" spacing={4}>
            <Flex gap={3} wrap="wrap" align="end">
              <Box minW={{ base: '100%', sm: '260px' }} flex="1">
                <AppInput
                  label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Asset, type, owner, village, reg…"
                />
              </Box>
              <Box minW={{ base: '100%', sm: '240px' }}>
                <AppSelect
                  label="Show"
                  value={typeFilter}
                  onChange={(v) => setTypeFilter(v || 'match')}
                  options={[
                    {
                      value: 'match',
                      label: `Matches only · ${requestTypeLabel}`,
                    },
                    { value: 'all', label: 'All available' },
                  ]}
                />
              </Box>
            </Flex>
            <Text fontSize="13px" color="text.secondary">
              {loading
                ? 'Loading…'
                : `${rows.length} shown · ${matchCount} match${matchCount === 1 ? '' : 'es'}`}{' '}
              · {selected.size} of {slots} slots
            </Text>
            <Text fontSize="12px" color="text.secondary">
              Match = equipment type matches “{requestTypeLabel}” (by type id or
              name). Switch to All available to browse other types.
            </Text>

            {slots === 0 ? (
              <StatusAlertCard
                title={`Recommendation limit reached (${maxTotal})`}
                body="Remove an existing recommendation on this line before adding another."
              />
            ) : null}

            <Box
              borderWidth="1px"
              borderColor="border.subtle"
              borderRadius="md"
              overflow="hidden"
            >
              <Box overflowX="auto">
                <Table variant="simple" size="md" sx={{ tableLayout: 'auto' }}>
                  <Thead bg="bg.input">
                    <Tr>
                      <Th w="52px" px={3} />
                      <Th whiteSpace="nowrap">Match</Th>
                      <Th whiteSpace="nowrap">Asset</Th>
                      <Th minW="180px">Equipment</Th>
                      <Th minW="140px">Type</Th>
                      <Th minW="140px">Owner</Th>
                      <Th minW="120px">Village</Th>
                      <Th whiteSpace="nowrap">Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map(({ asset, owner, kind }) => {
                      const isOn = selected.has(asset.assetId);
                      const isFull = !isOn && selected.size >= slots;
                      const badge = matchBadgeProps(kind);
                      const typeLabel = assetTypeLabel(asset);
                      const typeIsMatch = labelsMatch(typeLabel, requestTypeLabel);
                      return (
                        <React.Fragment key={asset.assetId}>
                          <Tr
                            cursor={isFull ? 'not-allowed' : 'pointer'}
                            opacity={isFull ? 0.5 : 1}
                            bg={isOn ? 'bg.amberTint' : undefined}
                            _hover={{ bg: isOn ? 'bg.amberTint' : 'bg.input' }}
                            onClick={() => toggle(asset.assetId)}
                          >
                            <Td
                              w="52px"
                              px={3}
                              py={3}
                              verticalAlign="top"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                size="lg"
                                isChecked={isOn}
                                isDisabled={isFull}
                                onChange={() => toggle(asset.assetId)}
                                colorScheme="yellow"
                              />
                            </Td>
                            <Td py={3} whiteSpace="nowrap" verticalAlign="top">
                              <StatusBadge status={badge.status}>
                                {badge.label}
                              </StatusBadge>
                            </Td>
                            <Td
                              py={3}
                              fontFamily="mono"
                              fontSize="13px"
                              whiteSpace="nowrap"
                              verticalAlign="top"
                            >
                              {asset.assetId}
                            </Td>
                            <Td py={3} verticalAlign="top" minW="180px">
                              <Text fontWeight="700" fontSize="15px">
                                {asset.equipmentDescription ||
                                  asset.equipmentType ||
                                  '—'}
                              </Text>
                              <Text fontSize="13px" color="text.secondary" mt={0.5}>
                                {[asset.manufacturer, asset.model, asset.regNo]
                                  .filter(Boolean)
                                  .join(' · ') || '—'}
                              </Text>
                            </Td>
                            <Td py={3} verticalAlign="top" minW="140px">
                              {typeLabel ? (
                                <Text
                                  fontSize="14px"
                                  fontWeight={typeIsMatch ? '700' : '500'}
                                  color={
                                    typeIsMatch
                                      ? 'status.success'
                                      : 'text.primary'
                                  }
                                >
                                  {typeLabel}
                                </Text>
                              ) : (
                                <Text fontSize="14px" color="text.secondary">
                                  —
                                </Text>
                              )}
                            </Td>
                            <Td py={3} verticalAlign="top" minW="140px">
                              <Text fontWeight="600" fontSize="14px">
                                {owner.ownerName || '—'}
                              </Text>
                              <Text fontSize="12px" color="text.secondary" mt={0.5}>
                                {owner.ownerType || ''}
                              </Text>
                            </Td>
                            <Td py={3} verticalAlign="top" minW="120px">
                              <Text fontSize="14px">
                                {owner.villageName || '—'}
                              </Text>
                            </Td>
                            <Td py={3} whiteSpace="nowrap" verticalAlign="top">
                              <StatusBadge
                                status={operationalBadge(asset.operationalStatus)}
                              >
                                {asset.operationalStatus}
                              </StatusBadge>
                            </Td>
                          </Tr>
                          {isOn ? (
                            <Tr bg="bg.amberTint">
                              <Td />
                              <Td colSpan={7} pt={0} pb={3}>
                                <Flex
                                  gap={3}
                                  align="flex-start"
                                  wrap={{ base: 'wrap', md: 'nowrap' }}
                                >
                                  <Box flex={{ base: '1 1 100%', md: '0 0 35%' }} minW={0}>
                                    <ReferredByPicker
                                      value={
                                        referrals[asset.assetId] || {
                                          appUserId: null,
                                          name: '',
                                        }
                                      }
                                      onChange={(next) =>
                                        setReferrals((prev) => ({
                                          ...prev,
                                          [asset.assetId]: next,
                                        }))
                                      }
                                    />
                                  </Box>
                                  <Box flex={{ base: '1 1 100%', md: '1 1 65%' }} minW={0}>
                                    <AppInput
                                      label="Recommendation remark"
                                      value={remarks[asset.assetId] || ''}
                                      onChange={(e) =>
                                        setRemarks((prev) => ({
                                          ...prev,
                                          [asset.assetId]: e.target.value,
                                        }))
                                      }
                                      maxLength={250}
                                      placeholder="Why this machine"
                                      h="44px"
                                      size="md"
                                    />
                                  </Box>
                                </Flex>
                              </Td>
                            </Tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                    {!loading && rows.length === 0 ? (
                      <Tr>
                        <Td colSpan={8}>
                          <Text color="text.secondary" py={8} textAlign="center">
                            {typeFilter === 'match'
                              ? `No verified available vehicles with type “${requestTypeLabel}”. Switch to All available to browse other types.`
                              : search.trim()
                                ? 'No vehicles match this search.'
                                : 'No available verified vehicles.'}
                          </Text>
                        </Td>
                      </Tr>
                    ) : null}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton
            type="button"
            onClick={handleRecommend}
            isDisabled={selected.size < 1}
            isLoading={saving}
          >
            Recommend ({selected.size}/{maxTotal})
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
